import { SkyTabList } from "@skypointcloud/skypoint-ui";
import { useLayoutEffect, useRef } from "react";
import { useLocation } from "wouter";
import { usePermissions } from "@/hooks/use-permissions";
import { SETTINGS_TABS, absolutePath, routeFor, type SettingsTab } from "@/lib/routes";

/**
 * The strip of the one Settings page (canvas 08; round 3, owner 2026-09-30: "if it's settings,
 * remove a dedicated destination and have the master name as Settings"): General | Destination |
 * Notifications, read from `SETTINGS_TABS`.
 *
 * General and Destination are separate ROUTES (`/settings` and `/settings/destination`, B-51a:
 * one prefix for every tab) because they carry different permissions, but to the reader they are tabs of one page titled
 * "Settings". The tab therefore navigates rather than switching a local state, so a link to any
 * tab still lands on it and the back button works. A tab the reader may not open (a Viewer holds
 * `instance.binding.read` only) is not offered; the tab they are on always is, and until their
 * permissions are known every tab is.
 *
 * Every destination goes through `absolutePath`. `App.tsx` mounts every section with wouter's
 * `nest`, so a bare `navigate("/settings/destination")` from inside `/settings` resolves against that
 * section's base and lands on `/settings/settings/destination`, which matches nothing. This strip
 * crosses sections in both directions, so it is exactly the case `absolutePath` exists for.
 *
 * Notifications (contract-notif R10) is a nested route of `/settings`, so it inherits that
 * guard. Connectors (T-51) is nested the same way and also asks for its own key (`anyPermission`
 * on its `SETTINGS_TABS` entry), so an InstanceAdmin is not offered it. The library's tabs take no per-tab test id, so the Notifications trigger is given
 * `notifications-tab` after render (§4.6), found by the id Radix derives from the tab's value.
 */
export function SettingsTabs({ active }: { active: SettingsTab }) {
  const [, setLocation] = useLocation();
  const { canAny, isResolved } = usePermissions();
  const listRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const trigger = listRef.current?.querySelector('[role="tab"][id$="-trigger-notifications"]');
    trigger?.setAttribute("data-testid", "notifications-tab");
  });

  const mayOpen = (tab: (typeof SETTINGS_TABS)[number]): boolean => {
    const route = routeFor(tab.path);
    if (route?.anyPermission && !canAny(...route.anyPermission)) return false;
    return !tab.anyPermission || canAny(...tab.anyPermission);
  };
  const tabs = SETTINGS_TABS.filter((tab) => tab.value === active || !isResolved || mayOpen(tab));

  return (
    <SkyTabList
      ref={listRef}
      testId="settings-tabs"
      selectedValue={active}
      tabs={tabs.map((tab) => ({ value: tab.value, label: tab.label }))}
      onTabSelect={(value) => {
        if (value === active) return;
        const tab = SETTINGS_TABS.find((candidate) => candidate.value === value);
        if (tab) setLocation(absolutePath(tab.path));
      }}
    />
  );
}
